import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { useTournament } from '@/context/TournamentContext';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function PickSelection() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { currentTournament, selectedTournamentId } = useTournament();

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedTournamentId) return;

      try {
        const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(userRes.data);

        const teamsRes = await axios.get(
          `${API}/tournaments/${selectedTournamentId}/teams`
        );
        setTeams(teamsRes.data);

        // Load existing picks
        try {
          const picksRes = await axios.get(
            `${API}/picks/my?tournament_id=${selectedTournamentId}`,
            { withCredentials: true }
          );
          const existingSelections = {};
          picksRes.data.forEach((pick) => {
            existingSelections[pick.seed] = pick.team_id;
          });
          setSelections(existingSelections);
        } catch (err) {
          // No picks yet
        }
      } catch (error) {
        toast.error('Failed to load tournament data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTournamentId]);

  const handleTeamSelect = (seed, teamId) => {
    // Prevent selection if picks are locked
    if (currentTournament?.picks_locked) {
      toast.error('Picks are locked for this tournament');
      return;
    }
    setSelections({ ...selections, [seed]: teamId });
  };

  const handleSubmit = async () => {
    // Check if picks are locked
    if (currentTournament?.picks_locked) {
      toast.error('Picks are locked for this tournament');
      return;
    }

    // Validate all 16 seeds selected
    const missingSeeds = [];
    for (let seed = 1; seed <= 16; seed++) {
      if (!selections[seed]) {
        missingSeeds.push(seed);
      }
    }

    if (missingSeeds.length > 0) {
      toast.error(`Please select teams for seeds: ${missingSeeds.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      const picks = Object.entries(selections).map(([seed, teamId]) => ({
        team_id: teamId,
        seed: parseInt(seed),
      }));

      await axios.post(
        `${API}/picks`,
        {
          tournament_id: selectedTournamentId,
          picks,
        },
        { withCredentials: true }
      );

      toast.success('Picks submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Picks are locked for this tournament');
      } else {
        toast.error('Failed to submit picks');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Group teams by seed
  const teamsBySeed = {};
  teams.forEach((team) => {
    if (!teamsBySeed[team.seed]) {
      teamsBySeed[team.seed] = [];
    }
    teamsBySeed[team.seed].push(team);
  });

  const allPicksComplete = () => {
    for (let seed = 1; seed <= 16; seed++) {
      if (!selections[seed]) return false;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-emerald-900 rounded-md p-8 mb-8 card-shadow text-white">
          <h1 className="font-bebas text-5xl tracking-wider mb-2">Make Your Picks</h1>
          <p className="font-manrope text-emerald-100">
            {currentTournament?.name || `${currentTournament?.year} Tournament`}
          </p>
          
          {currentTournament?.picks_locked ? (
            <div className="bg-red-600 border-2 border-red-700 rounded-md p-4 mt-4 flex items-start gap-3">
              <div className="text-white text-3xl flex-shrink-0">🔒</div>
              <div className="font-manrope text-white">
                <p className="font-bold text-lg mb-2">
                  Picks Are Locked
                </p>
                <p className="text-base">
                  The tournament has started and picks can no longer be changed or submitted.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-600 border-2 border-amber-700 rounded-md p-4 mt-4 flex items-start gap-3">
              <div className="text-white text-3xl flex-shrink-0">⚠️</div>
              <div className="font-manrope text-white">
                <p className="font-bold text-lg mb-2">
                  Select ONE team from each seed (1-16)
                </p>
                <p className="text-base">
                  Picks lock at tipoff of the first game on <strong>Thursday, March 19th</strong>. You may make changes up until then, but you <strong>MUST click SUBMIT</strong> for your changes to be saved.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-md card-shadow p-6">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16].map((seed) => {
              const seedTeams = teamsBySeed[seed] || [];
              const selectedTeamId = selections[seed];
              const isComplete = !!selectedTeamId;
              const isLocked = currentTournament?.picks_locked;

              return (
                <div key={seed} className={`border border-gray-200 rounded-md p-4 ${isLocked ? 'opacity-75' : ''}`}>
                  <div className="flex items-center mb-3">
                    <div className="w-16 h-16 bg-emerald-900 rounded-full flex items-center justify-center mr-4">
                      <span className="font-bebas text-2xl text-white">{seed}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bebas text-xl text-emerald-900">Seed {seed}</h3>
                      <p className="font-manrope text-sm text-gray-600">
                        {isLocked ? (
                          <span className="flex items-center text-red-600">
                            🔒 Locked
                          </span>
                        ) : isComplete ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle2 size={16} className="mr-1" />
                            Team selected
                          </span>
                        ) : (
                          'Select a team'
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {seedTeams.map((team) => {
                      const isSelected = selectedTeamId === team.team_id;
                      return (
                        <button
                          key={team.team_id}
                          onClick={() => handleTeamSelect(seed, team.team_id)}
                          disabled={isLocked}
                          className={`p-4 rounded-md border-2 transition-all font-manrope text-left relative ${
                            isSelected
                              ? isLocked
                                ? 'border-emerald-600 bg-emerald-50 shadow-md cursor-not-allowed'
                                : 'border-amber-600 bg-amber-50 shadow-md'
                              : isLocked
                              ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                              : 'border-gray-200 hover:border-emerald-600 hover:bg-gray-50'
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 size={20} className={isLocked ? 'text-emerald-600' : 'text-amber-600'} />
                            </div>
                          )}
                          <div className="font-bold text-emerald-900">{team.name}</div>
                          <div className="text-xs text-gray-600 mt-1">{team.region}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex justify-center">
            <Button
              onClick={handleSubmit}
              disabled={currentTournament?.picks_locked || !allPicksComplete() || submitting}
              className={`px-12 py-6 text-xl font-bebas tracking-wider ${
                !currentTournament?.picks_locked && allPicksComplete() && !submitting
                  ? 'btn-primary'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {currentTournament?.picks_locked
                ? '🔒 Picks Locked'
                : submitting
                ? 'Submitting...'
                : 'Submit All Picks'}
            </Button>
          </div>

          {currentTournament?.picks_locked ? (
            <p className="text-center mt-4 text-red-600 font-manrope text-sm font-bold">
              Tournament has started - picks are locked
            </p>
          ) : !allPicksComplete() ? (
            <p className="text-center mt-4 text-amber-600 font-manrope text-sm">
              Please complete all 16 picks before submitting
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
